/* eslint-disable jsx-a11y/no-static-element-interactions */
// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import { type SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v2/components/ProductFlags';
import ProductTags from '@jo-v2/components/ProductTags';
import { useEffect, useState, useMemo, memo, type MouseEventHandler } from 'react';
import { FaStar } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import useProductEvents from '@core/hooks/cohesion/useProductEvents';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
  selectRecommenderMatches,
} from '@core/reducers/matchesSlice';
import { mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';

import SchoolProgramTabs from '../SchoolProgramTabs';
import styles from './SchoolInfoSection.module.css';

type RecommenderCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withImage?: boolean;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
};

const SchoolInfoSection = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  withImage = false,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
}: RecommenderCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // determine if we are in single submission
  // DEV NOTE: setting this to just be true for the purposes of a desired optimization
  // and we don't want to remove the multi-select code just yet
  const isSingleSubmissionRecommender = true;
  const recommenderMatches = useSelector(selectRecommenderMatches);

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location:
        eventingOverrides?.location ||
        (recommenderMatches.length === 1 ? 'voyager-spotlight-pop-up' : 'voyager-recommender-pop-up'),
      formatType: 'app',
      formatSubtype: 'jo-recommendation',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  // Fire productLoaded + productViewed events and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    productLoaded();
    productViewed();
    if (result?.program?.id) {
      dispatch(
        setViewCorrelationIdMapEntry({
          key: result.program.id,
          value: newViewCorrelationId,
        })
      );
    }
  }, []);

  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  const onBtnClickHandler = async (event) => {
    // if (!event.target.classList.contains('tab-trigger')) {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'recommendation',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
    // }
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const handleProductClick = () => {
    const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  return (
    <section
      className={cn(styles.card, fullWidth ? styles.fullWidthCard : undefined)}
      onClick={isSingleSubmissionRecommender && onRecommender ? onBtnClickHandler : handleProductClick}
    >
      <div className={styles.bodyContainer}>
        <div className={styles.cardHeader}>
          <div className={styles.logo}>
            <img
              src={
                result?.school?.slug
                  ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                  : undefined
              }
              alt={result?.school?.name}
            />
          </div>
          <div>
            <h4 className={styles.title}>{result?.school?.name}</h4>
            {result?.school?.city && result?.school?.state && (
              <div className={styles.location}>
                <span className={styles.locationIcon}>
                  <HiLocationMarker size={16} />
                </span>{' '}
                {result?.school.city}, {result?.school.state}
              </div>
            )}
          </div>
          <button data-testid={`recommender-popup-card-button-${position}`} type="button" className={styles.cardBtnCta}>
            Explore Program
          </button>
        </div>
        <div className={styles.cardBody}>
          <button data-testid={`recommender-popup-card-button-${position}`} type="button" className={styles.cardBtnCta}>
            Explore Program
          </button>
        </div>
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(SchoolInfoSection);
